import React, { useState, useEffect, useCallback, useMemo } from 'react';
import referenceLabService from '../../services/referenceLabService';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './InvestigationMasterPage.css';

const INITIAL_MAPPING = {
  mappingId: 0,
  investigationId: '',
  referenceLabId: '',
  externalTestCode: '',
  externalTestName: '',
  specimenRequirement: 'Serum 2 mL',
  preferredMethod: 'CLIA / Automated',
  externalB2BRate: 0,
  expectedTatHours: 24,
  isActive: true
};

export const OutsourceMappingPage = ({ showToast }) => {
  const [viewMode, setViewMode] = useState('list');
  const [formData, setFormData] = useState(INITIAL_MAPPING);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [mappings, setMappings] = useState([]);
  const [referenceLabs, setReferenceLabs] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLabId, setSelectedLabId] = useState('all');
  const [selectedDeptId, setSelectedDeptId] = useState('all');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Form View Department & Search Filter (Requested by user)
  const [formDeptFilter, setFormDeptFilter] = useState('all');
  const [formTestSearch, setFormTestSearch] = useState('');

  // Load Reference Labs & Departments
  useEffect(() => {
    const loadPrerequisites = async () => {
      try {
        const [labsRes, deptsRes, invsRes] = await Promise.all([
          referenceLabService.getReferenceLabs({ isActive: true }),
          masterService.getDepartments({ isActive: true }),
          masterService.getInvestigations({ page: 1, pageSize: 1000 })
        ]);
        if (labsRes.success) setReferenceLabs(labsRes.data || []);
        if (deptsRes.success) setDepartments(deptsRes.data || []);
        if (invsRes.success) setInvestigations(invsRes.data?.items || invsRes.data || []);
      } catch (err) {
        console.error('Error loading prerequisites:', err);
      }
    };
    loadPrerequisites();
  }, []);

  // Fetch Mappings
  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await referenceLabService.getMappings({
        referenceLabId: selectedLabId === 'all' ? undefined : parseInt(selectedLabId, 10)
      });
      if (res.success && res.data) {
        setMappings(res.data || []);
      }
    } catch (err) {
      showToast?.('Failed to load outsource test mappings.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedLabId, showToast]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAddNew = () => {
    setFormData(INITIAL_MAPPING);
    setFormDeptFilter('all');
    setFormTestSearch('');
    setErrors({});
    setViewMode('form');
  };

  const handleEdit = (m) => {
    setFormData({
      mappingId: m.mappingId,
      investigationId: m.investigationId,
      referenceLabId: m.referenceLabId,
      externalTestCode: m.externalTestCode || '',
      externalTestName: m.externalTestName || '',
      specimenRequirement: m.specimenRequirement || 'Serum 2 mL',
      preferredMethod: m.preferredMethod || 'CLIA / Automated',
      externalB2BRate: m.externalB2BRate || 0,
      expectedTatHours: m.expectedTatHours || 24,
      isActive: m.isActive
    });
    const selectedInv = investigations.find(i => (i.investigationId || i.id) === m.investigationId);
    if (selectedInv?.subCategoryId) {
      setFormDeptFilter(String(selectedInv.subCategoryId));
    } else {
      setFormDeptFilter('all');
    }
    setFormTestSearch('');
    setErrors({});
    setViewMode('form');
  };

  const validate = () => {
    const errs = {};
    if (!formData.investigationId) errs.investigationId = 'Select an internal investigation.';
    if (!formData.referenceLabId) errs.referenceLabId = 'Select target reference laboratory.';
    if (!formData.externalTestCode.trim()) errs.externalTestCode = 'External Test Code is mandatory.';
    if (!formData.externalTestName.trim()) errs.externalTestName = 'External Test Name is mandatory.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) {
      showToast?.('Please resolve validation errors before saving.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        investigationId: parseInt(formData.investigationId, 10),
        referenceLabId: parseInt(formData.referenceLabId, 10),
        externalTestCode: formData.externalTestCode.trim(),
        externalTestName: formData.externalTestName.trim(),
        specimenRequirement: formData.specimenRequirement.trim() || null,
        preferredMethod: formData.preferredMethod.trim() || null,
        externalB2BRate: parseFloat(formData.externalB2BRate) || 0.00,
        expectedTatHours: parseInt(formData.expectedTatHours, 10) || 24,
        isActive: formData.isActive
      };

      await referenceLabService.saveMapping(payload);
      showToast?.('Outsource test mapping saved successfully.', 'success');
      await fetchMappings();
      setViewMode('list');
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Failed to save mapping.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (m) => {
    setDeleteTarget(m);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await referenceLabService.deleteMapping(deleteTarget.mappingId);
      showToast?.('Mapping deleted successfully.', 'success');
      setMappings(prev => prev.filter(item => item.mappingId !== deleteTarget.mappingId));
      setDeleteTarget(null);
    } catch (err) {
      showToast?.('Failed to delete mapping.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Filtered & Paginated items
  const filteredItems = useMemo(() => {
    return mappings.filter(item => {
      const matchesSearch = !searchQuery.trim() || 
        (item.investigationName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.investigationCode || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.externalTestCode || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.externalTestName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.referenceLabName || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDept = selectedDeptId === 'all' || item.subCategoryId === parseInt(selectedDeptId, 10);
      return matchesSearch && matchesDept;
    });
  }, [mappings, searchQuery, selectedDeptId]);

  // Form View: Department & Search Filtered Investigations (partitioned for fast lookup)
  const filteredInvestigations = useMemo(() => {
    return investigations.filter(inv => {
      const matchesDept = formDeptFilter === 'all' || String(inv.subCategoryId) === String(formDeptFilter);
      const matchesSearch = !formTestSearch.trim() ||
        (inv.name || '').toLowerCase().includes(formTestSearch.toLowerCase()) ||
        (inv.testCode || '').toLowerCase().includes(formTestSearch.toLowerCase()) ||
        (inv.shortName || '').toLowerCase().includes(formTestSearch.toLowerCase());
      return matchesDept && matchesSearch;
    });
  }, [investigations, formDeptFilter, formTestSearch]);

  // Group filtered investigations by clinical department
  const groupedInvestigations = useMemo(() => {
    const map = new Map();
    filteredInvestigations.forEach(inv => {
      const dept = inv.departmentName || 'General Diagnostics';
      if (!map.has(dept)) {
        map.set(dept, []);
      }
      map.get(dept).push(inv);
    });
    return Array.from(map.entries());
  }, [filteredInvestigations]);

  const {
    page,
    pageSize,
    totalRecords,
    setPage,
    setPageSize,
    paginatedItems: paginatedData,
  } = usePagination({
    items: filteredItems,
    initialPageSize: 25,
    resetDeps: [searchQuery, selectedLabId, selectedDeptId],
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? 'Configure Outsource Mapping' : 'Outsource Test Mapping Master'}
      masterName="Outsource Mapping"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form') handleAddNew();
        else setViewMode(mode);
      }}
    >
      {viewMode === 'form' ? (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSave} noValidate>
            <FormSection 
              title="01. Clinical Linkage & Department Partition" 
              description="Filter by department and search to link the exact internal investigation with target reference laboratory"
            >
              {/* Department & Quick Search Toolbar */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', 
                gap: '1rem', 
                padding: '1rem 1.25rem', 
                background: '#f8fafc', 
                border: '1px solid #e2e8f0', 
                borderRadius: '8px', 
                marginBottom: '1.25rem' 
              }}>
                {/* Step 1: Department Filter */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    Step 1: Department / Discipline Filter
                  </label>
                  <select
                    className="form-select"
                    value={formDeptFilter}
                    onChange={(e) => setFormDeptFilter(e.target.value)}
                    style={{ 
                      borderColor: formDeptFilter !== 'all' ? 'var(--primary-color)' : undefined,
                      fontWeight: formDeptFilter !== 'all' ? 600 : 'normal'
                    }}
                  >
                    <option value="all">🌐 All Clinical Departments ({departments.length})</option>
                    {departments.map(d => (
                      <option key={d.subCategoryId} value={d.subCategoryId}>
                        {d.displayName || d.name}
                      </option>
                    ))}
                  </select>
                  <span className="text-muted" style={{ fontSize: '11px', marginTop: '3px', display: 'block' }}>
                    Biochemistry, Hematology, Microbiology, Radiology, etc.
                  </span>
                </div>

                {/* Step 2: Instant Test Search Box */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    Step 2: Quick Search Test by Code / Name
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      style={{ paddingLeft: '2.2rem' }}
                      placeholder="Type test code or name (e.g. VIT, CBC, MRI)..."
                      value={formTestSearch}
                      onChange={(e) => setFormTestSearch(e.target.value)}
                    />
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                  <span className="text-muted" style={{ fontSize: '11px', marginTop: '3px', display: 'block' }}>
                    {filteredInvestigations.length} matching tests available below
                  </span>
                </div>
              </div>

              {/* Step 3: Investigation Select & Target Lab */}
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Step 3: Select Internal Investigation <span className="form-req">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.investigationId ? 'border-danger' : ''}`}
                    value={formData.investigationId}
                    onChange={(e) => {
                      const invId = e.target.value;
                      handleChange('investigationId', invId);
                      const selectedInv = investigations.find(i => (i.investigationId || i.id) === parseInt(invId, 10));
                      if (selectedInv) {
                        if (!formData.externalTestName || formData.externalTestName === INITIAL_MAPPING.externalTestName) {
                          handleChange('externalTestName', selectedInv.name);
                        }
                        if (selectedInv.primarySampleName && (!formData.specimenRequirement || formData.specimenRequirement === INITIAL_MAPPING.specimenRequirement)) {
                          handleChange('specimenRequirement', selectedInv.primarySampleName);
                        }
                      }
                    }}
                  >
                    <option value="">
                      {filteredInvestigations.length === 0 
                        ? '-- No investigations match this filter --' 
                        : `-- Select Internal Investigation (${filteredInvestigations.length} available) --`}
                    </option>
                    {groupedInvestigations.map(([deptName, invList]) => (
                      <optgroup key={deptName} label={`📁 ${deptName.toUpperCase()} (${invList.length} Tests)`}>
                        {invList.map(inv => (
                          <option key={inv.investigationId || inv.id} value={inv.investigationId || inv.id}>
                            [{inv.testCode}] {inv.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {errors.investigationId && <span className="error-message">{errors.investigationId}</span>}
                  
                  {formData.investigationId && (
                    <div style={{ marginTop: '6px', fontSize: '11.5px', color: '#15803d', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>✓ Selected:</span>
                      <strong>{investigations.find(i => (i.investigationId || i.id) === parseInt(formData.investigationId, 10))?.name}</strong>
                      <span style={{ color: 'var(--text-muted)' }}>
                        ({investigations.find(i => (i.investigationId || i.id) === parseInt(formData.investigationId, 10))?.departmentName})
                      </span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Step 4: Target Reference Lab <span className="form-req">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.referenceLabId ? 'border-danger' : ''}`}
                    value={formData.referenceLabId}
                    onChange={(e) => handleChange('referenceLabId', e.target.value)}
                  >
                    <option value="">-- Select Reference Laboratory ({referenceLabs.length} available) --</option>
                    {referenceLabs.map(lab => (
                      <option key={lab.referenceLabId} value={lab.referenceLabId}>
                        [{lab.labCode}] {lab.labName} ({lab.accreditation || 'Standard'})
                      </option>
                    ))}
                  </select>
                  {errors.referenceLabId && <span className="error-message">{errors.referenceLabId}</span>}
                </div>
              </div>
            </FormSection>

            <FormSection title="02. Partner Catalog & Protocols" description="Reference lab test code, analytical method and specimen handling instructions">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    External Partner Test Code <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.externalTestCode ? 'border-danger' : ''}`}
                    placeholder="e.g. QDL-VITD-25"
                    value={formData.externalTestCode}
                    onChange={(e) => handleChange('externalTestCode', e.target.value.toUpperCase())}
                  />
                  {errors.externalTestCode && <span className="error-message">{errors.externalTestCode}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    External Partner Test Name <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.externalTestName ? 'border-danger' : ''}`}
                    placeholder="e.g. 25-Hydroxy Vitamin D Total (CLIA)"
                    value={formData.externalTestName}
                    onChange={(e) => handleChange('externalTestName', e.target.value)}
                  />
                  {errors.externalTestName && <span className="error-message">{errors.externalTestName}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Analytical Method / Instrument</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. LC-MS/MS, CLIA, RT-PCR"
                    value={formData.preferredMethod}
                    onChange={(e) => handleChange('preferredMethod', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Specimen Requirements</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Fasting Serum 2 mL, Frozen (-20C)"
                    value={formData.specimenRequirement}
                    onChange={(e) => handleChange('specimenRequirement', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">External B2B Purchase Cost (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    placeholder="0.00"
                    value={formData.externalB2BRate}
                    onChange={(e) => handleChange('externalB2BRate', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Expected Partner TAT (Hours)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="24"
                    value={formData.expectedTatHours}
                    onChange={(e) => handleChange('expectedTatHours', e.target.value)}
                  />
                </div>
              </div>
            </FormSection>

            <div className="form-actions-bar" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Outsource Mapping'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          {/* Controls Bar */}
          <div className="filter-bar">
            <div className="filter-bar-left">
              <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '2.4rem' }}
                  placeholder="Search by Test, Partner Code or Reference Lab..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select"
                style={{ width: '220px', minWidth: '170px' }}
                value={selectedLabId}
                onChange={(e) => { setSelectedLabId(e.target.value); setPage(1); }}
              >
                <option value="all">All Reference Labs</option>
                {referenceLabs.map(lab => (
                  <option key={lab.referenceLabId} value={lab.referenceLabId}>{lab.labName}</option>
                ))}
              </select>

              <select
                className="form-select"
                style={{ width: '180px', minWidth: '150px' }}
                value={selectedDeptId}
                onChange={(e) => { setSelectedDeptId(e.target.value); setPage(1); }}
              >
                <option value="all">All Departments</option>
                {departments.map(d => (
                  <option key={d.subCategoryId} value={d.subCategoryId}>{d.displayName || d.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-bar-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddNew}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>+ Map Investigation</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive" style={{ marginTop: '1rem', overflowX: 'auto' }}>
            <table className="master-table mvc-table" style={{ width: '100%', minWidth: '1000px' }}>
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>Test Code</th>
                  <th style={{ minWidth: '180px' }}>Internal Investigation</th>
                  <th style={{ width: '130px' }}>Department</th>
                  <th style={{ minWidth: '180px' }}>Target Reference Lab</th>
                  <th style={{ minWidth: '220px' }}>External Code & Name</th>
                  <th style={{ minWidth: '150px' }}>Specimen & Method</th>
                  <th style={{ width: '120px', textAlign: 'right' }}>B2B Cost (₹)</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>TAT</th>
                  <th style={{ width: '130px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      Loading outsource test mappings...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      No outsource test mappings found. Click "+ Map Investigation" to link tests.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((m) => (
                    <tr key={m.mappingId}>
                      <td>
                        <span className="badge badge-secondary" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{m.investigationCode}</span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{m.investigationName}</div>
                      </td>
                      <td>
                        <span className="badge-outline" style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: '#f1f5f9', fontSize: '11px', fontWeight: 600 }}>
                          {m.departmentName || 'Diagnostics'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{m.referenceLabName}</div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>{m.referenceLabCode}</div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span className="badge badge-primary" style={{ fontFamily: 'monospace', fontSize: '11px', padding: '2px 6px' }}>{m.externalTestCode}</span>
                          <span>{m.externalTestName}</span>
                        </div>
                      </td>
                      <td>
                        <div>{m.specimenRequirement || 'Serum'}</div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>{m.preferredMethod || 'CLIA'}</div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>
                        ₹{Number(m.externalB2BRate || 0).toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {m.expectedTatHours} hrs
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(m)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(m)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[15, 25, 50, 100, 200]}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        title="Delete Outsource Mapping"
        message={`Are you sure you want to remove the mapping for "${deleteTarget?.investigationName}" to "${deleteTarget?.referenceLabName}"?`}
        confirmText="Delete Mapping"
        confirmVariant="danger"
        onConfirm={executeDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />
    </MasterPageWrapper>
  );
};

export default OutsourceMappingPage;
